import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, Lock, Truck } from 'lucide-react';
import type { JobWorkOrder } from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ListSkeleton,
  cn,
} from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { formatCurrency, formatDate } from '../lib/format.js';
import { useOutsidePremises, useVendors } from '../lib/outsourcing/use-outsourcing.js';

/**
 * What the shop does not currently have, and who has it.
 *
 * This screen leads with the PIECES rather than with the vendors, because the question that
 * brings somebody here is almost always "where is Priya's lehenga?" and only occasionally "what
 * do we owe Lakshmi?". Overdue rows are first and marked, since a piece that is late coming back
 * is a promised date the shop is about to miss and does not yet know it.
 *
 * The vendor list underneath carries what each is owed, which is the other half of the same
 * relationship — and a tap from there reaches the statement, where it gets settled.
 */
export function OutsourcingPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();

  // Every job-work order carries a rate, so a karigar is refused all of it.
  const allowed = role !== 'tailor';
  const outside = useOutsidePremises({ enabled: allowed });
  const vendors = useVendors({ enabled: allowed });

  if (!allowed) {
    return (
      <>
        <PageHeader title={t('outsourcing.title')} />
        <EmptyState
          icon={Lock}
          title={t('outsourcing.noAccessTitle')}
          description={t('outsourcing.noAccessHint')}
        />
      </>
    );
  }

  const data = outside.data;
  const jobs = [...(data?.jobs ?? [])].sort(
    (a, b) => Number(b.overdue) - Number(a.overdue) || (b.daysOut ?? 0) - (a.daysOut ?? 0),
  );

  return (
    <>
      <PageHeader title={t('outsourcing.title')} description={t('outsourcing.description')} />

      <div className="flex flex-col gap-6">
        {outside.isPending ? <ListSkeleton rows={4} /> : null}

        {data ? (
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Tile label={t('outsourcing.piecesOut')} value={String(data.pieceCount)} />
            <Tile
              label={t('outsourcing.overdue')}
              value={String(data.overdueCount)}
              tone={data.overdueCount > 0 ? 'warning' : undefined}
            />
            <Tile label={t('outsourcing.valueOut')} value={formatCurrency(data.totalValuePaise)} />
          </dl>
        ) : null}

        {data && jobs.length === 0 ? (
          <EmptyState
            icon={Truck}
            title={t('outsourcing.nothingOutTitle')}
            description={t('outsourcing.nothingOutDescription')}
          />
        ) : null}

        {jobs.length > 0 ? (
          <section className="flex flex-col gap-2" aria-label={t('outsourcing.outsideTitle')}>
            <h2 className="text-sm font-semibold">{t('outsourcing.outsideTitle')}</h2>
            <ul className="flex flex-col gap-1.5">
              {jobs.map((job) => (
                <li key={job.id}>
                  <JobRow job={job} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>{t('outsourcing.vendors')}</CardTitle>
          </CardHeader>
          <CardContent>
            {vendors.data && vendors.data.length > 0 ? (
              <ul className="flex flex-col gap-1.5">
                {vendors.data.map((vendor) => (
                  <li key={vendor.id}>
                    <Link
                      to={`/outsourcing/vendors/${vendor.id}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm hover:bg-accent"
                    >
                      <span className="min-w-0">
                        <span className="block font-medium">{vendor.name}</span>
                        <span className="block text-muted-foreground">
                          {vendor.specialisations
                            .map((one) => t(`outsourcing.workType.${one}`))
                            .join(', ')}
                        </span>
                      </span>
                      <span className="text-right">
                        <span className="block font-semibold tabular-nums">
                          {formatCurrency(vendor.outstandingPaise)}
                        </span>
                        {vendor.unappliedAdvancePaise > 0 ? (
                          <span className="block text-xs text-muted-foreground">
                            {t('outsourcing.advanceOut', {
                              amount: formatCurrency(vendor.unappliedAdvancePaise),
                            })}
                          </span>
                        ) : null}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">{t('outsourcing.noVendors')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warning';
}): ReactElement {
  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        tone === 'warning' ? 'border-destructive/40 bg-destructive/5' : 'border-border',
      )}
    >
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'text-xl font-semibold tabular-nums',
          tone === 'warning' && 'text-destructive',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function JobRow({ job }: { job: JobWorkOrder }): ReactElement {
  const { t } = useTranslation();
  const pending = job.pieces.filter((piece) => piece.outcome === 'pending');

  return (
    <Link
      to={`/outsourcing/vendors/${job.vendorId}`}
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 text-sm hover:bg-accent',
        job.overdue ? 'border-destructive/40 bg-destructive/5' : 'border-border',
      )}
    >
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{job.vendorName}</span>
          <Badge variant="outline">{t(`outsourcing.workType.${job.workType}`)}</Badge>
          {job.overdue ? (
            <Badge variant="destructive">
              <AlertTriangle aria-hidden="true" className="size-3" />
              {t('outsourcing.overdue')}
            </Badge>
          ) : null}
        </span>
        <span className="block text-muted-foreground">
          {job.number} · {t('outsourcing.pieceCount', { count: pending.length })}
          {job.promisedReturnDate
            ? ` · ${t('outsourcing.dueBack', { date: formatDate(job.promisedReturnDate) })}`
            : ''}
        </span>
        <span className="block text-muted-foreground">
          {pending
            .slice(0, 3)
            .map((piece) => `${piece.customerName} · ${piece.garmentTypeLabel}`)
            .join(', ')}
        </span>
      </span>
      <span className="text-right">
        <span className="block font-semibold tabular-nums">
          {t('outsourcing.daysOut', { count: job.daysOut ?? 0 })}
        </span>
        <span className="block text-xs text-muted-foreground tabular-nums">
          {formatCurrency(job.totalPaise)}
        </span>
      </span>
    </Link>
  );
}

export default OutsourcingPage;
